import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, TreePine, Globe2, X, ArrowRight, Sparkles } from 'lucide-react';
const STORAGE_KEY = 'zylus-intent-dismissed-v1';
const DELAY_MS = 45000; // 45 seconds
type Option = {
  id: string;
  icon: ComponentType<{
    className?: string;
  }>;
  title: string;
  desc: string;
  action: (navigate: ReturnType<typeof useNavigate>) => void;
};
const options: Option[] = [{
  id: 'home',
  icon: Home,
  title: 'A home for my family',
  desc: 'Browse premium houses and estates',
  action: navigate => navigate('/houses')
}, {
  id: 'land',
  icon: TreePine,
  title: 'Land to invest',
  desc: 'Verified plots with strong appreciation',
  action: navigate => navigate('/land')
}, {
  id: 'diaspora',
  icon: Globe2,
  title: "I'm buying from abroad",
  desc: 'Talk to Zee — our diaspora assistant',
  action: () => {
    window.dispatchEvent(new CustomEvent('open-zee-chat'));
  }
}];
export function IntentCapture() {
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    // Skip if already dismissed
    try {
      if (localStorage.getItem(STORAGE_KEY)) return;
    } catch {
      /* storage blocked — still show */
    }
    const timer = setTimeout(() => {
      setVisible(true);
    }, DELAY_MS);
    return () => clearTimeout(timer);
  }, []);
  // Hide on route change to /houses, /land, or /contact (likely already converted)
  useEffect(() => {
    if (['/houses', '/land', '/contact'].some(p => location.pathname.startsWith(p))) {
      setVisible(false);
    }
  }, [location.pathname]);
  const dismiss = (remember = true) => {
    setVisible(false);
    if (remember) {
      try {
        localStorage.setItem(STORAGE_KEY, String(Date.now()));
      } catch {
        /* noop */
      }
    }
  };
  const choose = (opt: Option) => {
    opt.action(navigate);
    dismiss(true);
  };
  return <AnimatePresence data-id="element-495">
      {visible && <>
          {/* Soft backdrop on mobile only */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => dismiss(true)} className="fixed inset-0 z-[45] bg-ink/40 backdrop-blur-[2px] sm:hidden" data-id="element-496" />

          <motion.div initial={{
        y: 80,
        opacity: 0,
        scale: 0.95
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 80,
        opacity: 0,
        scale: 0.95
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 26
      }} className="fixed z-50 left-3 right-3 bottom-3 sm:left-auto sm:right-5 sm:bottom-5 sm:w-[380px] bg-white rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.25)] border border-gold/30 overflow-hidden" data-id="element-497">
            {/* Decorative gold accent */}
            <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-gold/20 blur-3xl pointer-events-none" data-id="element-498" />

            {/* Header */}
            <div className="relative px-5 sm:px-6 pt-5 pb-3 flex items-start gap-3" data-id="element-499">
              <motion.div animate={{
            rotate: [0, 8, -8, 0]
          }} transition={{
            duration: 2.4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="w-10 h-10 rounded-2xl bg-gradient-to-br from-gold to-gold-dark flex items-center justify-center shrink-0 shadow-lg shadow-gold/30" data-id="element-500">
                <Sparkles className="w-5 h-5 text-ink" fill="currentColor" data-id="element-501" />
              </motion.div>
              <div className="flex-1 min-w-0 pt-1" data-id="element-502">
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-gold-dark mb-0.5" data-id="element-503">
                  Quick question
                </div>
                <div className="font-serif text-lg leading-tight text-ink" data-id="element-504">
                  What brought you to Zylus today?
                </div>
              </div>
              <button onClick={() => dismiss(true)} aria-label="Dismiss" className="w-7 h-7 rounded-full bg-bone hover:bg-ink hover:text-white text-ink/60 flex items-center justify-center transition-colors shrink-0" data-id="element-505">
                <X className="w-3.5 h-3.5" data-id="element-506" />
              </button>
            </div>

            {/* Options */}
            <div className="relative px-3 sm:px-4 pb-4 space-y-2" data-id="element-507">
              {options.map((opt, i) => {
            const Icon = opt.icon;
            return <motion.button key={opt.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1 + i * 0.07
            }} whileHover={{
              x: 3
            }} whileTap={{
              scale: 0.98
            }} onClick={() => choose(opt)} className="group w-full flex items-center gap-3 text-left bg-bone/50 hover:bg-ink hover:text-white rounded-2xl px-4 py-3 transition-colors border border-transparent hover:border-ink" data-id="element-508">
                    <div className="w-10 h-10 rounded-xl bg-white group-hover:bg-gold flex items-center justify-center shrink-0 transition-colors border border-ink/5" data-id="element-509">
                      <Icon className="w-5 h-5 text-ink" data-id="element-510" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-511">
                      <div className="font-semibold text-sm leading-tight" data-id="element-512">
                        {opt.title}
                      </div>
                      <div className="text-[11px] text-ink/55 group-hover:text-white/65 mt-0.5" data-id="element-513">
                        {opt.desc}
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-ink/30 group-hover:text-gold group-hover:translate-x-1 transition-all shrink-0" data-id="element-514" />
                  </motion.button>;
          })}
            </div>

            {/* Footer */}
            <div className="relative px-5 sm:px-6 pb-4 -mt-1" data-id="element-515">
              <button onClick={() => dismiss(true)} className="text-[11px] text-ink/40 hover:text-ink/70 transition-colors" data-id="element-516">
                Just browsing — don't ask again
              </button>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}